import type { Sample, Samples } from 'prove-common'
import { FetchError, getFirstRequisition, toLocaleDateString } from '../index'
import type {
  Geometry,
  Requisition,
  Result,
  ResultFilterEnum,
  ResultLines,
  SampleStatusEnum,
  StatusEvent,
  SubSamples
} from '../types/types'
import { useMemo } from 'react'
import { isFollowUpNull } from './requisitionUtils'

export const mapStateToSampleStatus = (
  state?: SampleStatusEnum
): SampleStatus | undefined => {
  switch (state) {
    case 'SampleCreated':
      return SampleStatus.sample_created
    case 'SampleSent':
      return SampleStatus.sample_sent
    case 'SampleReceived':
      return SampleStatus.sample_received
    case 'AnalysisStarted':
      return SampleStatus.analysis_started
    case 'ReadyToProcess':
      return SampleStatus.ready_to_process
    case 'Completed':
      return SampleStatus.completed
    case 'Closed':
      return SampleStatus.closed
    case 'Compromised':
      return SampleStatus.compromised
  }
}

export enum SampleStatus {
  missing_status = 'Mangler status',
  sample_created = 'Registrert',
  sample_sent = 'Sendt',
  sample_received = 'Mottatt lab',
  analysis_started = 'Analyse startet',
  ready_to_process = 'Klar til behandling',
  completed = 'Ferdigstilt',
  closed = 'Lukket',
  compromised = 'Kansellert'
}

export enum SampleResultStatus {
  negative = 'Ikke påvist',
  positive = 'Påvist',
  inconclusive = 'Inkonklusiv',
  cancelled = 'Kansellert'
}

export const getResultStatus = (
  result: Result | Requisition['results'] | undefined,
  compromised: boolean | undefined
): SampleResultStatus | undefined => {
  const status = result?.status
  if (status === 'NEGATIVE') return SampleResultStatus.negative
  if (status === 'POSITIVE') return SampleResultStatus.positive
  if (compromised) return SampleResultStatus.cancelled
}

export const getSampleStatus = (sample: Sample): SampleStatus =>
  mapStateToSampleStatus(sample.state) ?? SampleStatus.missing_status

const formatMilestoneTime = (
  timestamp: StatusEvent['timestamp'] | undefined,
  additionalInfo: StatusEvent['additionalInfo']
) => {
  if (additionalInfo?.startsWith('lifecycle:inferred-')) {
    return 'Tidspunkt ukjent (utledet)'
  }
  if (additionalInfo?.startsWith('lifecycle:late-observation')) {
    return 'Bekreftet i ettertid'
  }
  return timestamp ? toLocaleDateString(timestamp) : undefined
}

export const formatPosition = (coordinates?: number[]) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return '-'
  const [longitude, latitude] = coordinates
  if (typeof longitude !== 'number' || typeof latitude !== 'number') return '-'
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
}

export const geometryToLongitudeLatitude = (geometry?: Geometry) => {
  const coordinates = geometry?.coordinates
  if (!Array.isArray(coordinates) || coordinates.length < 2) return undefined
  const [longitude, latitude] = coordinates
  if (typeof longitude !== 'number' || typeof latitude !== 'number') {
    return undefined
  }
  return { longitude, latitude }
}

export const longitudeLatitudeToGeometry = ({
  longitude,
  latitude
}: {
  longitude: number
  latitude: number
}): Geometry => ({
  type: 'Point',
  coordinates: [longitude, latitude]
})

export const extractCoordinates = (
  pos: unknown
): { longitude: number; latitude: number } | undefined => {
  if (pos == null || typeof pos !== 'object') return undefined
  if ('coords' in pos && (pos as { coords: unknown }).coords != null) {
    const coords = (
      pos as { coords: { longitude?: unknown; latitude?: unknown } }
    ).coords
    if (
      typeof coords.longitude === 'number' &&
      typeof coords.latitude === 'number'
    ) {
      return { longitude: coords.longitude, latitude: coords.latitude }
    }
  }
  if ('type' in pos && 'coordinates' in pos) {
    return geometryToLongitudeLatitude(pos as Geometry)
  }
  if ('longitude' in pos && 'latitude' in pos) {
    const p = pos as { longitude?: unknown; latitude?: unknown }
    if (typeof p.longitude === 'number' && typeof p.latitude === 'number') {
      return { longitude: p.longitude, latitude: p.latitude }
    }
  }
  return undefined
}

export const areCoordinatesEqual = (
  c1?: { longitude: number; latitude: number } | null,
  c2?: { longitude: number; latitude: number } | null
): boolean => {
  if (!c1 && !c2) return true
  if (!c1 || !c2) return false
  return c1.longitude === c2.longitude && c1.latitude === c2.latitude
}

export const getSampleProgress = (sample: Sample) => {
  const requisition = getFirstRequisition(sample)
  const isCompromised = isSampleCompromised(sample)
  const hasActiveCancellation =
    isCompromised ||
    ((sample.subSamples?.length ?? 0) > 0 &&
      sample.subSamples!.every(({ compromised }) => compromised))
  const isCompleted = isSampleCompleted(sample)
  const isFollowUpNotNull = !isFollowUpNull(requisition)
  const statusEvents = sample.statuses ?? []
  const excludedStatuses = new Set<SampleStatusEnum>([
    'RequisitionProcessed',
    'ResultConfirmed',
    'ResultConclusionReset',
    'ResultCreated',
    'Closed'
  ])
  let requiredStatuses: SampleStatusEnum[]
  if (isCompromised || isCompleted) {
    requiredStatuses = ['SampleCreated', 'Completed']
  } else {
    requiredStatuses = [
      'SampleCreated',
      'SampleSent',
      'SampleReceived',
      'AnalysisStarted',
      'ReadyToProcess',
      'Completed'
    ]
  }

  const filteredStatusEvents: Partial<StatusEvent>[] = statusEvents
    .filter(({ status }) => !excludedStatuses.has(status))
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))

  let orderedStatusEvents: Partial<StatusEvent>[]
  if (isCompromised || isCompleted) {
    orderedStatusEvents = filteredStatusEvents
    requiredStatuses.forEach((status) => {
      if (!orderedStatusEvents.some((event) => event.status === status)) {
        orderedStatusEvents.push({
          status,
          timestamp: undefined,
          user: undefined
        })
      }
    })
  } else {
    // Keep workflow order during reassessment: the undone previous completion predates
    // the new ReadyToProcess milestone but still belongs at the end of progress.
    orderedStatusEvents = [
      ...requiredStatuses.map((status) => {
        const event = filteredStatusEvents.find((e) => e.status === status)
        return event ?? { status, timestamp: undefined, user: undefined }
      })
    ]
    filteredStatusEvents
      .filter(
        (event) => !requiredStatuses.includes(event.status as SampleStatusEnum)
      )
      .forEach((event) => {
        // Skip gaps before earlier recorded workflow steps; an undone completion
        // still belongs last and must not move cancellation past pending steps.
        const nextMilestone = orderedStatusEvents.findIndex(
          (milestone, index) =>
            milestone.status === 'Completed' ||
            (event.timestamp != null &&
              milestone.timestamp != null &&
              milestone.timestamp > event.timestamp) ||
            (milestone.timestamp == null &&
              !orderedStatusEvents
                .slice(index + 1)
                .some(
                  (laterMilestone) =>
                    laterMilestone.status !== 'Completed' &&
                    laterMilestone.timestamp != null &&
                    event.timestamp != null &&
                    laterMilestone.timestamp <= event.timestamp
                ))
        )
        orderedStatusEvents.splice(
          nextMilestone < 0 ? orderedStatusEvents.length : nextMilestone,
          0,
          event
        )
      })
  }

  const progress = orderedStatusEvents.map(
    ({ status, timestamp, additionalInfo }) => ({
      label:
        status === 'Compromised' && !hasActiveCancellation
          ? 'Kansellert (angret)'
          : (mapStateToSampleStatus(status)?.toString() ?? status ?? '-'),
      isCompleted: timestamp != undefined,
      completedAt: formatMilestoneTime(timestamp, additionalInfo),
      isWarning: status === 'Compromised' && hasActiveCancellation,
      isUndo:
        (status === 'Compromised' && !hasActiveCancellation) ||
        (status === 'Completed' &&
          !isSampleCompleted(sample) &&
          isFollowUpNotNull)
    })
  )

  // Always insert takenDate as the first step
  if (sample.takenDate) {
    progress.unshift({
      label: 'Uttak',
      isCompleted: true,
      completedAt: toLocaleDateString(sample.takenDate),
      isWarning: false,
      isUndo: false
    })
  }

  return progress
}

export const isSample = (sample?: Sample): sample is Sample => sample != null

export const isSampleNotFound = (error: Error | null) =>
  error instanceof FetchError && error.status === 404

export const findSampleWithId = (
  sampleId: string,
  samples: Samples
): Sample | undefined => samples?.find((sample) => sample.id === sampleId)

export const findSamplesByTaskId = (
  taskId: number,
  samples: Samples
): Samples | undefined => samples?.filter((sample) => sample.taskId === taskId)

export const isSampleRegistered = (sampleId: string, samples: Samples) =>
  samples.some((sample) => sample.id === sampleId)

export const hasAllSubSamplesBeenAnalyzed = (
  { results }: Requisition,
  subSamples: SubSamples
): boolean => {
  return subSamples
    .filter(({ compromised }) => !compromised)
    .every(
      ({ subSampleId }) =>
        (results?.subSamples?.[subSampleId]?.resultSets?.length ?? 0) > 0
    )
}

export const isAnalysisCompleted = ({ state }: Sample) =>
  state === 'ReadyToProcess'

export const hasSampleBeenAnalyzed = ({ statuses }: Sample) =>
  statuses?.map((status) => status.status).includes('ReadyToProcess') ?? false

export const hasSampleBeenCompromised = (sample?: Sample) =>
  sample?.subSamples?.every(({ compromised }) => compromised) || false

export const getSampleResultBlockers = (
  sample: Sample,
  { results }: Requisition
) => {
  const missingResults: number[] = []
  const unconcludedResults: number[] = []
  const activeSubSamples = (sample.subSamples ?? []).filter(
    ({ compromised }) => !compromised
  )
  activeSubSamples.forEach(({ subSampleId }) => {
    const resultSets = results?.subSamples?.[subSampleId]?.resultSets ?? []
    if (resultSets.length === 0) missingResults.push(subSampleId)
    else if (
      resultSets.some(
        ({ status }) => status !== 'NEGATIVE' && status !== 'POSITIVE'
      )
    ) {
      unconcludedResults.push(subSampleId)
    }
  })
  return {
    missingResults,
    unconcludedResults,
    activeSubSampleCount: activeSubSamples.length
  }
}

export const isSampleReadyToBeFinalized = (
  sample: Sample,
  requisition: Requisition
): boolean => {
  const { missingResults, unconcludedResults, activeSubSampleCount } =
    getSampleResultBlockers(sample, requisition)
  if (isSampleCompromised(sample)) {
    return (sample.subSamples?.length ?? 0) > 0 && activeSubSampleCount === 0
  }
  return (
    isSampleReadyToProcess(sample) &&
    activeSubSampleCount > 0 &&
    missingResults.length === 0 &&
    unconcludedResults.length === 0
  )
}

const isSampleReadyToProcess = ({ state }: Sample) => state === 'ReadyToProcess'

export const isSampleCompleted = ({ state }: Sample) => state === 'Completed'

export const isSampleCompromised = ({ state }: Sample) =>
  state === 'Compromised'

export const isSampleSent = ({ state }: Sample) => state === 'SampleSent'

export const calculateTotalSubSamplesAnalysed = (samples: Samples) =>
  samples.reduce((total, sample) => {
    if (
      isSampleCompleted(sample) ||
      (isAnalysisCompleted(sample) && hasSampleBeenAnalyzed(sample))
    ) {
      return total + (sample.subSamples?.length ?? 0)
    }
    return total
  }, 0)

export const calculateTotalSamplesPaavist = (samples: Samples) => {
  let count = 0
  samples.forEach((sample) => {
    const requisition = getFirstRequisition(sample)
    if (!requisition) return

    // Check main sample result
    const isCompromised = hasSampleBeenCompromised(sample)
    const mainStatus = getResultStatus(requisition.results, isCompromised)
    if (mainStatus === SampleResultStatus.positive) {
      count++
    }

    // Check subsample results
    const subSamples = sample.subSamples ?? []
    subSamples.forEach((subSample) => {
      const subSampleResult = requisition.results?.subSamples?.[subSample.id]
      const subStatus = getResultStatus(subSampleResult, subSample.compromised)
      if (subStatus === SampleResultStatus.positive) {
        count++
      }
    })
  })
  return count
}

export const statusFilterOptions: { const: SampleStatusEnum; title: string }[] =
  [
    {
      const: 'SampleCreated',
      title: 'Registrert'
    },
    {
      const: 'SampleSent',
      title: 'Sendt'
    },
    {
      const: 'SampleReceived',
      title: 'Mottatt lab'
    },
    {
      const: 'AnalysisStarted',
      title: 'Analyse startet'
    },
    {
      const: 'ReadyToProcess',
      title: 'Klar til behandling'
    },
    {
      const: 'Completed',
      title: 'Ferdigstilt'
    },
    {
      const: 'Compromised',
      title: 'Kansellert'
    }
  ]

export const resultFilterOptions: {
  const: ResultFilterEnum
  title: string
}[] = [
  {
    const: 'NEGATIVE',
    title: 'Ikke påvist'
  },
  {
    const: 'POSITIVE',
    title: 'Påvist'
  },
  {
    const: 'CANCELLED',
    title: 'Kansellert'
  }
]

export const getStatusFilterTitle = <T extends string>(status: T) =>
  statusFilterOptions.find((option) => option.const === status)?.title ?? status

export const getResultFilterTitle = (result: ResultFilterEnum) =>
  resultFilterOptions.find((option) => option.const === result)?.title ?? result

export const getResultLines = (result: Result | undefined): ResultLines =>
  result?.resultSets?.flatMap(({ resultLines }) => resultLines || []) || []

export function parseSampleIds(value: string): string[] {
  if (!value) return []
  const trimmed = value.trim()
  if (trimmed === '') return []
  return trimmed
    .replace(/, *$/, '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

export function useSampleIds(searchValue: string): string[] {
  return useMemo(() => parseSampleIds(searchValue), [searchValue])
}

export const isSampleEditable = (sample: Sample | undefined): boolean => {
  return (
    sample?.state === 'SampleCreated' ||
    sample?.state === 'SampleSent' ||
    sample?.state === 'SampleReceived'
  )
}

export const isSamplePartiallyEditable = (
  sample: Sample | undefined
): boolean => {
  return sample?.state === 'Completed' || sample?.state === 'ReadyToProcess'
}

export const getSampleBusinessDisplayName = (
  sample: Pick<
    Sample,
    'businessName' | 'oversightObject' | 'externalReferenceId'
  >
): string | undefined => {
  return sample.businessName ?? sample.oversightObject
}
